// src/store/slices/admin/settingSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getSettings = createAsyncThunk(
    'settings/getSettings',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.get(API_ENDPOINTS.SETTINGS, { params });
            const { status, message, data } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Settings retrieved', data };
            } else {
                return rejectWithValue({ message: message || 'Failed to fetch settings', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch settings';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const createSetting = createAsyncThunk(
    'settings/createSetting',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.post(API_ENDPOINTS.SETTINGS + '/create', data);
            const { status, message, data: responseData } = response.data;
            if (status === 201 || status === 200) {
                return { success: true, message: message || 'Setting created', setting: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to create setting', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to create setting';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const updateSetting = createAsyncThunk(
    'settings/updateSetting',
    async ({ key, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.put(API_ENDPOINTS.SETTING(key), data);
            const { status, message, data: responseData } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Setting updated', setting: responseData };
            } else {
                return rejectWithValue({ message: message || 'Failed to update setting', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to update setting';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.data?.errors) {
                    const errorMessages = Object.values(error.response.data.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

export const deleteSetting = createAsyncThunk(
    'settings/deleteSetting',
    async (key, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');
            const response = await apiClient.delete(API_ENDPOINTS.SETTING(key));
            const { status, message } = response.data;
            if (status === 200) {
                return { success: true, message: message || 'Setting deleted', key };
            } else {
                return rejectWithValue({ message: message || 'Failed to delete setting', status });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete setting';
            if (error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }
            return rejectWithValue({ message: errorMessage, status: error.response?.status || 500 });
        }
    }
);

// =============================================
// SLICE
// =============================================

const initialState = {
    items: [], // list of settings (may be grouped)
    grouped: {},
    isLoading: false,
    isSubmitting: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

const settingSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
        clearSuccessMessage: (state) => { state.successMessage = null; },
        clearApiMessage: (state) => { state.apiMessage = null; },
        clearState: (state) => {
            state.items = [];
            state.grouped = {};
            state.isLoading = false;
            state.isSubmitting = false;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getSettings.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getSettings.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload;
                const responseData = payload.data || {};
                state.items = responseData || [];
                state.grouped = responseData || {};
                state.successMessage = payload.message || 'Settings retrieved';
                state.apiMessage = payload.message || 'Settings retrieved';
                state.error = null;
            })
            .addCase(getSettings.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch settings';
                state.apiMessage = action.payload?.message || 'Failed to fetch settings';
                state.successMessage = null;
                state.items = [];
                state.grouped = {};
            })
            .addCase(createSetting.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createSetting.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.successMessage = action.payload.message || 'Setting created';
                state.apiMessage = action.payload.message || 'Setting created';
                state.error = null;
                // Refresh list
                // Could also push new item
            })
            .addCase(createSetting.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create setting';
                state.apiMessage = action.payload?.message || 'Failed to create setting';
                state.successMessage = null;
            })
            .addCase(updateSetting.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateSetting.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.successMessage = action.payload.message || 'Setting updated';
                state.apiMessage = action.payload.message || 'Setting updated';
                state.error = null;
            })
            .addCase(updateSetting.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update setting';
                state.apiMessage = action.payload?.message || 'Failed to update setting';
                state.successMessage = null;
            })
            .addCase(deleteSetting.pending, (state) => {
                state.isSubmitting = true;
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deleteSetting.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.successMessage = action.payload.message || 'Setting deleted';
                state.apiMessage = action.payload.message || 'Setting deleted';
                state.error = null;
            })
            .addCase(deleteSetting.rejected, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete setting';
                state.apiMessage = action.payload?.message || 'Failed to delete setting';
                state.successMessage = null;
            });
    }
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearState,
} = settingSlice.actions;

export const selectSettings = (state) => state.settings?.items || [];
export const selectSettingsGrouped = (state) => state.settings?.grouped || {};
export const selectSettingsLoading = (state) => state.settings?.isLoading || false;
export const selectSettingsSubmitting = (state) => state.settings?.isSubmitting || false;
export const selectSettingsError = (state) => state.settings?.error || null;
export const selectSettingsApiMessage = (state) => state.settings?.apiMessage || null;

export default settingSlice.reducer;